import { inject, Injectable } from "@angular/core";
import FileSaver from "file-saver";
import { map, Observable, tap } from "rxjs";
import { HttpService } from "src/app/session/services/http.service";
import { environment } from "src/environments/environment";

import { GeneralInfoData } from "../details/history-log/data-input-log/models/general-info";
import {
  Item,
  ItemData,
  ItemInputField,
  ItemSection,
} from "../details/history-log/data-input-log/models/item";
import {
  ScopeOfWorkData,
  ScopeOfWorkInputField,
  ScopeOfWorkSection,
} from "../details/history-log/data-input-log/models/scope-of-work";
import { EventHistoryApi, EventHistoryDocument } from "../model/event-history";

@Injectable({
  providedIn: "root",
})
export class AuditService {
  private readonly httpService = inject(HttpService);

  public async getEventsHistoryData(
    inspectionId: string,
    pageNumber: number,
    pageSize: number,
  ): Promise<EventHistoryApi[]> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/eventsHistory?page=${pageNumber}&pageSize=${pageSize}`;
    return this.httpService.get(url, { withCredentials: true }).toPromise();
  }

  public async downloadAttachedDocument(
    inspectionId: string,
    document: EventHistoryDocument,
  ): Promise<void> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/downloadDocument/${document.id}`;
    const blob = await this.httpService
      .get(url, { responseType: "blob", withCredentials: true })
      .toPromise();
    FileSaver.saveAs(blob, document.name);
  }

  public getGeneralInfoData(
    inspectionId: string,
    pageNumber: number,
    pageSize: number,
  ): Observable<GeneralInfoData[]> {
    const url = `${environment.webApiUrl}/api/Audit/generalInfo/${inspectionId}/dataInputLog?page=${pageNumber}&pageSize=${pageSize}`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getItemsData(
    inspectionId: string,
    itemId: string,
    sectionId: string,
    fieldId: string,
    pageNumber: number,
    pageSize: number,
  ): Observable<ItemData[]> {
    const url = `${environment.webApiUrl}/api/audit/${inspectionId}/Items/Events?itemId=${itemId}&sectionId=${sectionId}&fieldId=${fieldId}&page=${pageNumber}&pageSize=${pageSize}`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getScopeOfWorkData(
    inspectionId: string,
    sectionId: string,
    fieldId: string,
    pageNumber: number,
    pageSize: number,
  ): Observable<ScopeOfWorkData[]> {
    const url = `${environment.webApiUrl}/api/audit/${inspectionId}/ScopeOfWork/Events?sectionId=${sectionId}&fieldId=${fieldId}&page=${pageNumber}&pageSize=${pageSize}`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getItems(inspectionId: string): Observable<Item[]> {
    const url = `${environment.webApiUrl}/api/inspectionDetails/itemList-light/${inspectionId}`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getItemSections(
    inspectionId: string,
    itemId: string,
  ): Observable<ItemSection[]> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/Item/${itemId}/Sections`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getScopeOfWorkSections(
    inspectionId: string,
  ): Observable<ScopeOfWorkSection[]> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/ScopeOfWork/Sections`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getItemsInputFields(
    inspectionId: string,
    itemId: string,
    sectionId: string,
  ): Observable<ItemInputField[]> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/Item/${itemId}/${sectionId}/Fields`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public getScopeOfWorkInputFields(
    inspectionId: string,
    sectionId: string,
  ): Observable<ScopeOfWorkInputField[]> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/ScopeOfWork/${sectionId}/Fields`;
    return this.httpService.get(url, { withCredentials: true });
  }

  public downloadGeneralInfoDocument(inspectionId: string): Observable<void> {
    const url = `${environment.webApiUrl}/api/Audit/GeneralInfo/${inspectionId}/DataInputLog/Download`;
    return this.httpService.getWithHeaders(url, { responseType: "blob" }).pipe(
      tap((response) => {
        const contentDisposition = response.headers
          .get("content-disposition")
          .match(/filename="(.+?)"/)[1];
        const fileName = contentDisposition.substring(
          0,
          contentDisposition.lastIndexOf("."),
        );
        FileSaver.saveAs(response.body, fileName);
      }),
      map(() => void 0),
    );
  }

  public downloadItemsDocument(
    inspectionId: string,
    itemId: string,
    sectionId: string,
    fieldId: string,
  ): Observable<void> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/Items/Download?itemId=${itemId}&sectionId=${sectionId}&fieldId=${fieldId}`;
    return this.httpService.getWithHeaders(url, { responseType: "blob" }).pipe(
      tap((response) => {
        const contentDisposition = response.headers
          .get("content-disposition")
          .match(/filename="(.+?)"/)[1];
        const fileName = contentDisposition.substring(
          0,
          contentDisposition.lastIndexOf("."),
        );
        FileSaver.saveAs(response.body, fileName);
      }),
      map(() => void 0),
    );
  }

  public downloadScopeOfWorkDocument(
    inspectionId: string,
    sectionId: string,
    fieldId: string,
  ): Observable<void> {
    const url = `${environment.webApiUrl}/api/Audit/${inspectionId}/ScopeOfWork/Download?sectionId=${sectionId}&fieldId=${fieldId}`;
    return this.httpService.getWithHeaders(url, { responseType: "blob" }).pipe(
      tap((response) => {
        const contentDisposition = response.headers
          .get("content-disposition")
          .match(/filename="(.+?)"/)[1];
        const fileName = contentDisposition.substring(
          0,
          contentDisposition.lastIndexOf("."),
        );
        FileSaver.saveAs(response.body, fileName);
      }),
      map(() => void 0),
    );
  }
}
